import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Send, Wallet } from 'lucide-react';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, PageSkeleton, useToast } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { usePayout, useSendStatement, useStatementDownload } from '../lib/payouts/use-payouts.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * The tailor's copy of what they were paid, and why.
 *
 * Every job is a row with its rate and its amount, every addition and deduction carries the
 * reason it was recorded with, and an advance being recovered says when it was taken. The total
 * is the sum of what is on the page and nothing else — a statement a person cannot check is a
 * statement they will not believe, and this is the screen that settles arguments.
 *
 * It is printable as it stands (the chrome is hidden by `print:` rules) and shareable as a PDF
 * over WhatsApp, because the karigar's own copy has to leave the shop's screen to be worth
 * anything.
 */
export function PayoutStatementPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { payoutId = '' } = useParams<{ payoutId: string }>();
  const { role } = useAuth();

  const payout = usePayout(payoutId);
  const download = useStatementDownload();
  const send = useSendStatement();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const canSend = role === 'owner' || role === 'manager';

  if (payout.isPending) return <PageSkeleton label={t('common.loading')} />;
  if (!payout.data) {
    return (
      <EmptyState
        icon={Wallet}
        title={t('payouts.statementNotFound')}
        description={t('payouts.statementNotFoundHint')}
      />
    );
  }

  const data = payout.data;

  const fetchPdf = async (): Promise<void> => {
    try {
      const link = await download.mutateAsync(data.id);
      setPdfUrl(link.url);
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  const share = async (): Promise<void> => {
    try {
      await send.mutateAsync({ payoutId: data.id });
      toast({ title: t('payouts.statementSent', { name: data.staffName }), variant: 'success' });
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      {/* Back to where the reader came from: their own earnings, or the person's ledger. */}
      <div className="pb-2 print:hidden">
        <Button asChild variant="ghost" size="sm">
          <Link to={canSend ? `/payouts/staff/${data.staffUserId}` : '/my-earnings'}>
            <ArrowLeft aria-hidden="true" />
            {canSend ? t('payouts.backToStaff') : t('payouts.backToMyEarnings')}
          </Link>
        </Button>
      </div>

      <PageHeader
        title={t('payouts.statementTitle', { number: data.number })}
        description={t('payouts.statementFor', {
          name: data.staffName,
          from: formatDate(data.periodFrom),
          to: formatDate(data.periodTo),
        })}
        action={
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer aria-hidden="true" />
              {t('payouts.print')}
            </Button>
            {pdfUrl ? (
              <Button asChild variant="outline">
                <a href={pdfUrl} target="_blank" rel="noreferrer">
                  <Download aria-hidden="true" />
                  {t('payouts.openPdf')}
                </a>
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={download.isPending}
                onClick={() => void fetchPdf()}
              >
                <Download aria-hidden="true" />
                {t('payouts.downloadPdf')}
              </Button>
            )}
            {canSend ? (
              <Button disabled={send.isPending} onClick={() => void share()}>
                <Send aria-hidden="true" />
                {t('payouts.share')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="flex flex-col gap-5">
        {/* --- Every job, its rate, its amount ------------------------------------------- */}
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold">{t('payouts.jobsHeading')}</h2>
          <ul className="flex flex-col gap-2">
            {data.jobs.map((job) => (
              <li
                key={job.assignmentId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <span className="min-w-0">
                  <span className="block font-medium">
                    {job.quantity} × {job.garmentTypeLabel} · {t(`stage.${job.stage}`)}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {job.orderNumber} · {job.customerName} ·{' '}
                    {t('payouts.finishedOn', { date: formatDate(job.completedAt) })}
                  </span>
                </span>
                <span className="text-right">
                  <span className="block font-semibold tabular-nums">
                    {formatCurrency(job.amountPaise)}
                  </span>
                  <span className="block text-xs text-muted-foreground tabular-nums">
                    {t('payouts.rateEach', { rate: formatCurrency(job.ratePaise) })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* --- How the figure was reached -------------------------------------------------- */}
        <section className="flex flex-col gap-2 rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold">{t('payouts.workingHeading')}</h2>
          <dl className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between gap-3">
              <dt>{t('payouts.jobCount', { count: data.jobs.length })}</dt>
              <dd className="tabular-nums">{formatCurrency(data.jobsPaise)}</dd>
            </div>

            {data.adjustments.map((adjustment) => (
              <div key={adjustment.entryId} className="flex justify-between gap-3">
                <dt>
                  {t(`payouts.kind.${adjustment.kind}`)} — {adjustment.reason}
                </dt>
                <dd className="tabular-nums">
                  {adjustment.kind === 'deduction' ? '−' : '+'}
                  {formatCurrency(adjustment.amountPaise)}
                </dd>
              </div>
            ))}

            {data.advanceApplications.map((application) => (
              <div key={application.entryId} className="flex justify-between gap-3">
                <dt>{t('payouts.advanceTakenOn', { date: formatDate(application.advancedOn) })}</dt>
                <dd className="tabular-nums">−{formatCurrency(application.amountPaise)}</dd>
              </div>
            ))}

            <div className="flex justify-between gap-3 border-t border-border pt-2 text-base font-semibold">
              <dt>{t('payouts.paidTotal')}</dt>
              <dd className="tabular-nums">{formatCurrency(data.netPaise)}</dd>
            </div>
          </dl>

          <p className="text-sm text-muted-foreground">
            {t('payouts.paidBy', {
              method: t(`payments.methods.${data.method}`),
              date: formatDate(data.paidAt),
            })}
            {data.settledByName ? ` · ${data.settledByName}` : ''}
          </p>
          {data.note ? <p className="text-sm">{data.note}</p> : null}
          {data.statementSentAt ? (
            <p className="text-sm text-muted-foreground print:hidden">
              {t('payouts.alreadySent', { date: formatDate(data.statementSentAt) })}
            </p>
          ) : null}
        </section>

        <p className="text-sm text-muted-foreground">{t('payouts.checkItHint')}</p>
      </div>
    </>
  );
}

export default PayoutStatementPage;
